export const CATEGORY_DIMS = {
	orientation: [
		'creative_orientation',
		'data_orientation',
		'leadership_orientation',
		'people_orientation',
		'structured_orientation',
	],
	interest: [
		'artistic_interest',
		'investigative_interest',
		'realistic_interest',
		'social_interest',
		'enterprising_interest',
	],
	aptitude: [
		'analytical_aptitude',
		'creative_aptitude',
		'spatial_aptitude',
		'technical_aptitude',
		'verbal_aptitude',
	],
	personality: [
		'openness_trait',
		'conscientiousness_trait',
		'extraversion_trait',
		'agreeableness_trait',
		'emotional_stability',
	],
	eq: [
		'empathy_eq',
		'motivation_eq',
		'self_awareness_eq',
		'social_skills_eq',
		'stress_management',
	],
}

export const CATEGORY_LABELS = {
	orientation: 'Work Style',
	interest: 'Core Interest',
	aptitude: 'Cognitive Aptitude',
	personality: 'Personality',
	eq: 'EQ Strength',
}

export const DIM_LABELS = {
	creative_orientation: 'Creative Orientation',
	data_orientation: 'Data Orientation',
	leadership_orientation: 'Leadership Orientation',
	people_orientation: 'People Orientation',
	structured_orientation: 'Structured Orientation',
	artistic_interest: 'Artistic Interest',
	investigative_interest: 'Investigative Interest',
	realistic_interest: 'Realistic Interest',
	social_interest: 'Social Interest',
	enterprising_interest: 'Enterprising Interest',
	analytical_aptitude: 'Analytical Aptitude',
	creative_aptitude: 'Creative Aptitude',
	spatial_aptitude: 'Spatial Aptitude',
	technical_aptitude: 'Technical Aptitude',
	verbal_aptitude: 'Verbal Aptitude',
	openness_trait: 'Openness',
	conscientiousness_trait: 'Conscientiousness',
	extraversion_trait: 'Extraversion',
	agreeableness_trait: 'Agreeableness',
	emotional_stability: 'Emotional Stability',
	empathy_eq: 'Empathy',
	motivation_eq: 'Motivation',
	self_awareness_eq: 'Self-Awareness',
	social_skills_eq: 'Social Skills',
	stress_management: 'Stress Management',
}

const TYPE_NAMES = {
	creative_orientation: 'Creative Innovator',
	data_orientation: 'Analytical Researcher',
	leadership_orientation: 'Strategic Director',
	people_orientation: 'Human-Centric Lead',
	structured_orientation: 'Systems Architect',
	artistic_interest: 'Expressive Visionary',
	investigative_interest: 'Scholarly Researcher',
	realistic_interest: 'Technical Specialist',
	social_interest: 'Community Impact Advocate',
	enterprising_interest: 'Dynamic Venture Lead',
	analytical_aptitude: 'Logic Strategist',
	creative_aptitude: 'Original Ideator',
	spatial_aptitude: 'Dimensional Designer',
	technical_aptitude: 'Mechanics Specialist',
	verbal_aptitude: 'Eloquent Communicator',
	openness_trait: 'Curious Explorer',
	conscientiousness_trait: 'Reliable Executor',
	extraversion_trait: 'Social Catalyst',
	agreeableness_trait: 'Empathic Collaborator',
	emotional_stability: 'Calm Navigator',
	empathy_eq: 'Empathy Anchor',
	motivation_eq: 'Drive Engine',
	self_awareness_eq: 'Reflective Strategist',
	social_skills_eq: 'Connector',
	stress_management: 'Pressure Manager',
}

const ALL_DIMS = Object.values(CATEGORY_DIMS).flat()

const DIM_TO_CATEGORY = Object.fromEntries(
	Object.entries(CATEGORY_DIMS).flatMap(([cat, dims]) => dims.map(d => [d, cat])),
)

const getTypeLabel = dim => ({
	name: TYPE_NAMES[dim] || DIM_LABELS[dim] || 'Archetype',
	tags: [
		(DIM_LABELS[dim] || 'Strength').split(' ')[0].toUpperCase(),
		'POTENTIAL',
		'GROWTH',
	],
	blurb: `${DIM_LABELS[dim] || 'This dimension'} is one of the student strengths in this profile.`,
})

export const buildProfileFromTraitScores = traitScores => {
	const profile = {}
	if (!traitScores || typeof traitScores !== 'object') return profile
	const hasQuestionKeys = ALL_DIMS.some(dim =>
		Number.isFinite(Number(traitScores?.[`${dim}_q1`])) ||
		Number.isFinite(Number(traitScores?.[`${dim}_q2`])),
	)
	const hasDirectDimKeys = ALL_DIMS.some(dim =>
		Number.isFinite(Number(traitScores?.[dim])),
	)

	for (const dim of ALL_DIMS) {
		if (hasQuestionKeys) {
			let s = 0
			for (let i = 1; i <= 2; i++) {
				const k = `${dim}_q${i}`
				const v = Number(traitScores[k])
				if (Number.isFinite(v)) s += Math.min(v, 5)
			}
			profile[dim] = Math.min(s, 10)
			continue
		}
		if (hasDirectDimKeys) {
			const raw = Number(traitScores?.[dim])
			if (!Number.isFinite(raw)) {
				profile[dim] = 0
				continue
			}
			profile[dim] = raw <= 10 ? Math.max(0, raw) : Math.max(0, Math.min(10, raw / 10))
			continue
		}
		profile[dim] = 0
	}
	return profile
}

export const rankDimsInCategory = (profile, category) =>
	(CATEGORY_DIMS[category] || [])
		.map(d => ({ dim: d, score: Number(profile?.[d] ?? 0) }))
		.sort((a, b) => b.score - a.score)

export const rankAllDims = profile =>
	ALL_DIMS.map(d => ({
		dim: d,
		score: Number(profile?.[d] ?? 0),
		category: DIM_TO_CATEGORY[d],
	})).sort((a, b) => b.score - a.score)

export const buildPersonalityType = (profile, fallback = null) => {
	if (fallback?.name && (fallback?.description || fallback?.tags?.length)) return fallback
	const top = rankAllDims(profile).find(r => r.score > 0)
	if (!top) return null
	const primary = getTypeLabel(top.dim)
	return {
		name: primary.name,
		description: primary.blurb,
		tags: primary.tags,
		dim: top.dim,
		category: DIM_TO_CATEGORY[top.dim],
	}
}

export const buildSuperProfile = profile => {
	const out = {}
	for (const category of Object.keys(CATEGORY_DIMS)) {
		const top = rankDimsInCategory(profile, category)[0]
		if (!top) continue
		out[category] = {
			dim: top.dim,
			score: top.score,
			categoryLabel: CATEGORY_LABELS[category],
			label: getTypeLabel(top.dim),
		}
	}
	return out
}

export const buildCampusVibe = profile => {
	const orient = rankDimsInCategory(profile, 'orientation')[0]?.dim
	const byOrient = {
		creative_orientation: 'You thrive where imagination and experimentation are encouraged.',
		data_orientation: 'You thrive in research-focused and structured learning environments.',
		leadership_orientation: 'You thrive on campuses with leadership and initiative opportunities.',
		people_orientation: 'You thrive in collaborative communities and mentoring-rich environments.',
		structured_orientation: 'You thrive in organized systems with clear expectations.',
	}
	return {
		blurb: byOrient[orient] || byOrient.data_orientation,
		chips: ['Creative Fields', 'Exploratory', 'Low Structure', 'Interdisciplinary'],
	}
}

const SUPERPOWER_COPY = {
	creative_orientation: 'You find fresh angles quickly and bring novel ideas to execution.',
	data_orientation: 'You turn complexity into clear decisions with evidence-first thinking.',
	leadership_orientation: 'You naturally organize teams and move projects forward.',
	people_orientation: 'You build trust and collaboration around shared goals.',
	structured_orientation: 'You create reliable systems and consistent outcomes.',
}

const BLINDSPOT_COPY = {
	creative_orientation: 'Stretch by adding structure to your strongest ideas.',
	data_orientation: 'Stretch by acting faster when complete data is unavailable.',
	leadership_orientation: 'Stretch by practicing listening-first leadership moments.',
	people_orientation: 'Stretch by balancing empathy with clear boundaries.',
	structured_orientation: 'Stretch by experimenting beyond familiar frameworks.',
}

export const buildSuperpowers = (profile, n = 2) =>
	rankAllDims(profile)
		.filter(r => r.score > 0)
		.slice(0, n)
		.map(r => ({
			dim: r.dim,
			dimLabel: DIM_LABELS[r.dim],
			score: r.score,
			copy: SUPERPOWER_COPY[r.dim] || 'A strength worth leaning into.',
		}))

export const buildBlindspots = (profile, { n = 2, maxScore = 5 } = {}) =>
	[...rankAllDims(profile)]
		.reverse()
		.filter(r => r.score > 0 && r.score < maxScore)
		.slice(0, n)
		.map(r => ({
			dim: r.dim,
			dimLabel: DIM_LABELS[r.dim],
			score: r.score,
			copy: BLINDSPOT_COPY[r.dim] || 'An area to grow progressively.',
		}))

export const buildExpandingStrengths = profile => {
	const ranked = rankAllDims(profile)
	const N = ranked.length || 1
	return ranked.map((r, i) => {
		const absPct = Math.max(0, Math.min(100, (r.score / 10) * 100))
		const rankPct = ((N - i - 0.5) / N) * 100
		const percent = Math.max(0, Math.min(100, Math.round(0.5 * absPct + 0.5 * rankPct)))
		return {
			dim: r.dim,
			dimLabel: DIM_LABELS[r.dim],
			archetypeName: getTypeLabel(r.dim).name,
			archetypeBlurb: getTypeLabel(r.dim).blurb,
			tags: getTypeLabel(r.dim).tags,
			score: r.score,
			percent,
			category: r.category,
		}
	})
}

